import {get, has, includes, isEmpty, keys, set, some} from 'lodash';
import moment from 'moment';

import {userApi} from 'api/user';
import {firebase} from 'config/firebase';
import {NO_AUTH_ROUTE_PATHS, ParentRoutes, ROUTE_PATH} from 'constants/routes';
import {history} from 'context/history';
import {AuthSource, LogoutType, UserPasswordExpireStatus} from 'enums';
import {authDataStore, guestUserInfoSchemaStore, SharedPromise, userInfoSchemaStore} from 'services';
import {AuthData, User, UserPermissions, BaseUserRole, AuthDataStore} from 'types';

export const RETURN_URL_QUERY = 'returnUrl';

const refreshAccessTokenExpiresIn = 30000; // 30 seconds in milliseconds
let shouldDeleteFirebaseTokenOnReset = true;

const getLocalUser = (): User | null => authDataStore.getValue().localUser;

const setLocalUser = (localUser: User) => {
    authDataStore.mergeValue({localUser});
};

const getUserPermissions = () => authDataStore.getValue().localUserPermissions;

const getUserDefaultPage = () => authDataStore.getValue().defaultPage;

const resetLocalData = (storeUpdates?: Partial<AuthDataStore>) => {
    if (shouldDeleteFirebaseTokenOnReset) {
        firebase.deleteToken();
        shouldDeleteFirebaseTokenOnReset = false;
    }
    authDataStore.resetValue(storeUpdates);
    // The schemas are account-scoped and are re-fetched only while the store is empty, so keeping them across a
    // logout shows the previous account's fields to the next user until the page is fully reloaded
    userInfoSchemaStore.resetValue();
    guestUserInfoSchemaStore.resetValue();
};

const userHasRole = (user: {roles?: string[]}, role: string): boolean => includes(user?.roles, role);

const userHasInSessionRole = (user: {roles?: string[]}, role: string): boolean => includes(user?.roles, role);

const localUserHasInSessionRole = (role: string): boolean => includes(getLocalUser().inSessionRoles, role);

const isAccountAdmin = (user = getLocalUser()) => userHasRole(user, BaseUserRole.Admin) || userHasRole(user, BaseUserRole.AppAdmin);

const isAppAdmin = (user = getLocalUser()) => userHasRole(user, BaseUserRole.AppAdmin);

const parseJwtPayload = (token: string) => {
    if (!token) {
        return null;
    }
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
};

const logout = (logoutType: LogoutType) => {
    const tokenExpired = logoutType === LogoutType.TokenExpired;
    const sessionTokenExpired = logoutType === LogoutType.SessionTokenExpired;
    authDataStore.mergeValue({logoutInProgress: true});
    if (!NO_AUTH_ROUTE_PATHS.includes(window.location.pathname)) {
        const searchParams = new URLSearchParams(history.location.search);
        const returnUrl = searchParams.get(RETURN_URL_QUERY);
        history.push(ROUTE_PATH.PUBLIC.LOGIN,
            {tokenExpired, previousPath: sessionTokenExpired ? null : returnUrl || window.location.pathname});
    }
    resetLocalData({logoutType});
    // TODO: check if refresh token exist for sessionTokenExpired and do logout on backend?
    return tokenExpired || sessionTokenExpired ? Promise.resolve() : userApi.logout();
};

const getAuthSource = (): AuthSource => authDataStore.getValue().authSource;

const isMobileAppAuthSource = (): boolean => [AuthSource.AndroidSDK, AuthSource.IosSDK].includes(getAuthSource());

const isFirebaseSupportedAuthSource = () => !isMobileAppAuthSource();

const isDownloadFileDisabledAuthSource = () => isMobileAppAuthSource();

const isAndroidSDKAuthSource = (): boolean => AuthSource.AndroidSDK === getAuthSource();

const setAuthData = (authData: AuthData) => {
    const accessToken = authData.accessToken;
    const storeUpdates: Partial<AuthDataStore> = {accessToken, logoutType: null};
    const {exp, aud} = parseJwtPayload(accessToken);
    if (exp) {
        storeUpdates.accessTokenExpirationDate = exp * 1000;
    }
    storeUpdates.authSource = aud;
    authDataStore.mergeValue(storeUpdates);
    shouldDeleteFirebaseTokenOnReset = isFirebaseSupportedAuthSource();
};

const getAccessToken = (): string => authDataStore.getValue().accessToken;

const setAccessToken = (token: string) => setAuthData({accessToken: token, refreshToken: null});

const isLoggedIn = () => !!getAccessToken();

const refreshAccessToken = () => new SharedPromise(async () => {
    const existence = await userApi.checkRefreshTokenExistence();
    if (!existence?.exist) {
        if (isLoggedIn()) {
            logout(LogoutType.TokenExpired);
        }
        return Promise.reject('Refresh token does not exist');
    }
    const authData = await userApi.refreshAccessToken();
    setAuthData(authData);
}).run();

const accessTokenExpiresSoon = (): boolean => {
    const accessTokenExpirationDate = authDataStore.getValue()?.accessTokenExpirationDate;
    if (accessTokenExpirationDate) {
        return (accessTokenExpirationDate - Date.now()) < refreshAccessTokenExpiresIn;
    }
    return false;
};

const fetchCurrentUser = async () => {
    const user = await userApi.getProfile();
    setLocalUser(user);
};

const getUserPasswordExpiresInDays = (
    userPasswordSettings: {days: number, soonDays: number},
    user = getLocalUser()
) => {
    if (!userPasswordSettings?.days || !user?.passwordChangedAt) {
        return null;
    }
    const passwordLifeDays = moment().diff(user.passwordChangedAt, 'd');
    return Math.max(userPasswordSettings.days - passwordLifeDays, 0);
};

const getUserPasswordExpireStatus = (
    userPasswordSettings: {days: number, soonDays: number},
    timeZone: string,
    user = getLocalUser()
): UserPasswordExpireStatus => {
    if (!userPasswordSettings?.days || !user?.passwordChangedAt) {
        return UserPasswordExpireStatus.Ok;
    }
    const passwordChangedAt = moment(user.passwordChangedAt).tz(timeZone);

    const passwordExpired = moment().tz(timeZone)
        .subtract(userPasswordSettings.days, 'd')
        .isAfter(passwordChangedAt.startOf('d'));

    if (passwordExpired) {
        return UserPasswordExpireStatus.Expired;
    }

    const passwordExpireSoon = moment().tz(timeZone)
        .subtract(userPasswordSettings.days, 'd')
        .add(userPasswordSettings.soonDays, 'd')
        .isAfter(passwordChangedAt.endOf('d'));

    if (passwordExpireSoon) {
        return UserPasswordExpireStatus.ExpireSoon;
    }

    return UserPasswordExpireStatus.Ok;
};

const isRouteEnabled = (path: string[], permissions = getUserPermissions()): boolean => {
    if (isEmpty(path)) {
        return true;
    }
    return get(permissions, [...path, 'enabled'], false);
};

const hasEnabledChild = (permission = {} as any): boolean => {
    if (has(permission, 'enabled')) {
        return permission.enabled;
    }

    const objectKeys = keys(permission);
    return some(objectKeys, (key) => hasEnabledChild(permission[key]));
};

const isParentRouteEnabled = (parent: string, permissions = getUserPermissions()): boolean => {
    if (!parent) {
        return true;
    }
    const routeObject = permissions?.[parent];
    if (isEmpty(routeObject)) {
        return false;
    } else {
        return hasEnabledChild(routeObject);
    }
};

const setUserPermissions = (localUserPermissions: UserPermissions = {}, authorized = false) => {
    let defaultPage: string = null;
    if (localUserPermissions?.passwordExpired?.enabled) {
        defaultPage = ROUTE_PATH.USER.PASSWORD.EXPIRED;
    } else if (localUserPermissions?.accountBlocked?.enabled) {
        defaultPage = ROUTE_PATH.USER.ACCOUNT_BLOCKED._SELF;
    } else if (localUserPermissions?.accountActivation?.enabled) {
        defaultPage = ROUTE_PATH.USER.ACCOUNT_ACTIVATION._SELF;
    } else if (localUserPermissions?.online?.enabled) {
        defaultPage = ROUTE_PATH.USER.ONLINE._SELF;
    } else if (localUserPermissions?.account?.myProfile?.enabled) {
        defaultPage = ROUTE_PATH.USER.ACCOUNT.MY_PROFILE;
    } else {
        if (isParentRouteEnabled(ParentRoutes.Activity, localUserPermissions)) {
            defaultPage = ROUTE_PATH.USER.ACTIVITY._SELF;
        } else if (isParentRouteEnabled(ParentRoutes.Account, localUserPermissions)) {
            defaultPage = ROUTE_PATH.USER.ACCOUNT._SELF;
        } else if (isParentRouteEnabled(ParentRoutes.Content, localUserPermissions)) {
            defaultPage = ROUTE_PATH.USER.CONTENT._SELF;
        }
    }
    if (authorized) {
        set(localUserPermissions, ['about', 'me', 'enabled'], true);
    }
    authDataStore.mergeValue({localUserPermissions, defaultPage});
};

export const userAuthUtils = {
    logout,
    setAuthData,
    isLoggedIn,
    isAccountAdmin,
    isAppAdmin,
    userHasRole,
    userHasInSessionRole,
    localUserHasInSessionRole,
    getLocalUser,
    setLocalUser,
    fetchCurrentUser,
    getUserDefaultPage,
    resetLocalData,
    getAccessToken,
    setAccessToken,
    getAuthSource,
    isAndroidSDKAuthSource,
    isMobileAppAuthSource,
    isFirebaseSupportedAuthSource,
    isDownloadFileDisabledAuthSource,
    isRouteEnabled,
    isParentRouteEnabled,
    getUserPermissions,
    setUserPermissions,
    refreshAccessToken,
    accessTokenExpiresSoon,
    getUserPasswordExpireStatus,
    getUserPasswordExpiresInDays,
    parseJwtPayload
};
